import React, { useState, useEffect } from 'react';
import { Client } from '../../types/clientIntelligence';

interface ClientSearchProps {
  clients: Client[];
  selectedClient: Client | null;
  onClientSelect: (client: Client) => void;
  onClearSelection: () => void;
}

export const ClientSearch: React.FC<ClientSearchProps> = ({
  clients,
  selectedClient,
  onClientSelect,
  onClearSelection
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);

  useEffect(() => {
    // Update search query when client is selected
    if (selectedClient) {
      setSearchQuery(`${selectedClient.first_name} ${selectedClient.last_name}`);
    }
  }, [selectedClient]);

  useEffect(() => {
    // Filter clients based on search query
    if (searchQuery.trim() && !selectedClient) {
      const filtered = clients.filter(client => 
        `${client.first_name} ${client.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredClients(filtered);
      setShowDropdown(true);
    } else {
      setFilteredClients([]);
      setShowDropdown(false);
    }
  }, [searchQuery, clients, selectedClient]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (selectedClient) {
      onClearSelection();
    }
  };

  const handleClientSelect = (client: Client) => {
    onClientSelect(client);
    setShowDropdown(false);
  };

  const handleClearSelection = () => {
    setSearchQuery('');
    onClearSelection();
  };

  return (
    <div className="relative">
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search clients by name or email..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
          />
          
          {/* Search Results Dropdown */}
          {showDropdown && filteredClients.length > 0 && (
            <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto mt-1">
              {filteredClients.map((client) => (
                <div
                  key={client.id}
                  onClick={() => handleClientSelect(client)}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <div className="font-medium text-gray-900">
                    {client.first_name} {client.last_name}
                  </div>
                  {client.email && (
                    <div className="text-sm text-gray-500">{client.email}</div>
                  )}
                  {client.company_name && (
                    <div className="text-sm text-gray-400">{client.company_name}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {selectedClient && (
          <button
            onClick={handleClearSelection}
            className="px-4 py-3 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}; 